import { throttle } from 'lodash';
import type { DebouncedFunc, ThrottleSettings } from 'lodash';

export type BufferedItem<T, A> = T extends object ? T & { _action?: A } : T;

export type ThrottledFuncWithBuffer<T, A = string> = DebouncedFunc<CallbackWrapper<T, A>>;

type Callback<T> = {
	(items: T[]): unknown;
};

type CallbackWrapper<T, A> = {
	(items: T[], action?: A): unknown;
};

type BufferingHandler<T, A> = {
	(items: T[], prevBuffer: BufferedItem<T, A>[], action?: A): BufferedItem<T, A>[];
};

type AutoFlushConfig = {
	onBufferSizeReached: number;
};

type BufferingOptions<T, A> = {
	dataBufferingHandler?: BufferingHandler<T, A>;
	autoFlush?: AutoFlushConfig;
};

export const throttleWithBuffer = <ItemType, BufferActions = string>(
	callback: Callback<BufferedItem<ItemType, BufferActions>>,
	wait: number,
	options: BufferingOptions<ItemType, BufferActions> = {},
	throttleOptions: ThrottleSettings = { leading: false, trailing: true }
) => {
	let dataBuffer: BufferedItem<ItemType, BufferActions>[] = [];

	const flushDataBuffer = () => (dataBuffer = []);
	const callbackWithDataFlush: Callback<BufferedItem<ItemType, BufferActions>> = items => {
		flushDataBuffer();
		return callback(items);
	};
	const throttledCallback: DebouncedFunc<Callback<ItemType>> = throttle(callbackWithDataFlush, wait, throttleOptions);
	const dataBufferingHandler: BufferingHandler<ItemType, BufferActions> =
		options.dataBufferingHandler ||
		(items => {
			dataBuffer.push(...(items as BufferedItem<ItemType, BufferActions>[]));
			return dataBuffer;
		});
	const throttleWrapper: ThrottledFuncWithBuffer<ItemType, BufferActions> = (items, action) => {
		dataBuffer = dataBufferingHandler(items, dataBuffer.slice(), action);

		const result = throttledCallback(dataBuffer);

		if (options.autoFlush?.onBufferSizeReached) {
			if (dataBuffer.length >= options.autoFlush.onBufferSizeReached) {
				throttleWrapper.flush();
			}
		}

		return result;
	};

	throttleWrapper.flush = () => {
		flushDataBuffer();
		return throttledCallback.flush();
	};

	throttleWrapper.cancel = () => {
		flushDataBuffer();
		return throttledCallback.cancel();
	};

	return throttleWrapper;
};
